import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Check, Edit2, AlertTriangle, Phone, FlaskConical, Download } from 'lucide-react';
import { getBandwidthRouting, saveBandwidthRouting, syncBandwidthRouting, fetchBandwidthNumbers } from '../services/portalApi';

function SipUriEditor({ number, current, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(current || '');

  const commit = async () => {
    await onSave(number, draft.trim());
    setEditing(false);
  };

  if (!editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {current
          ? <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--purple)' }}>{current}</span>
          : <span style={{ fontSize: 12, color: 'var(--muted-3)', fontStyle: 'italic' }}>Not configured</span>}
        <button
          onClick={() => { setDraft(current || ''); setEditing(true); }}
          style={{ background: 'none', border: '1px solid var(--border-strong)', borderRadius: 4, color: 'var(--muted-2)', cursor: 'pointer', padding: '2px 7px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 3 }}
        >
          <Edit2 size={10}/> Edit
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input
        autoFocus
        type="text"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        placeholder="sip:destination@host:5060"
        style={{
          background: 'var(--bg)', border: '1px solid var(--purple)', borderRadius: 4,
          color: 'var(--text-2)', padding: '4px 8px', fontSize: 12, width: 260, outline: 'none',
        }}
      />
      <button
        onClick={commit}
        style={{ background: 'var(--purple-bg)', border: '1px solid var(--purple)', borderRadius: 4, color: '#a5b4fc', cursor: 'pointer', padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 3, fontSize: 11 }}
      >
        <Check size={11}/> Save
      </button>
      <button
        onClick={() => setEditing(false)}
        style={{ background: 'none', border: '1px solid var(--border-strong)', borderRadius: 4, color: 'var(--muted-2)', cursor: 'pointer', padding: '4px 8px', fontSize: 11 }}
      >
        Cancel
      </button>
    </div>
  );
}

export default function BandwidthRouting() {
  const [numbers,   setNumbers]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [syncing,   setSyncing]   = useState(false);
  const [fetching,  setFetching]  = useState(false);
  const [error,     setError]     = useState(null);
  const [search,    setSearch]    = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getBandwidthRouting();
      setNumbers(data.numbers || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await syncBandwidthRouting();
      await load();
      if (r.added === 0 && r.discovered === 0) {
        alert('No Bandwidth CDRs found. Run a Bandwidth CDR sync first to discover numbers.');
      } else {
        alert(`Discovered ${r.discovered} number${r.discovered !== 1 ? 's' : ''}, added ${r.added} new.`);
      }
    } catch (e) {
      alert('Sync failed: ' + e.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleFetch = async () => {
    setFetching(true);
    try {
      const r = await fetchBandwidthNumbers();
      await load();
      alert(`Fetched ${r.fetched} number${r.fetched !== 1 ? 's' : ''} from Bandwidth, added ${r.added} new.`);
    } catch (e) {
      alert('Fetch failed: ' + e.message);
    } finally {
      setFetching(false);
    }
  };

  const handleSave = async (number, sipUri) => {
    await saveBandwidthRouting(number, { sip_uri: sipUri || null });
    setNumbers(prev => prev.map(n => n.phone_number === number ? { ...n, sip_uri: sipUri || null } : n));
  };

  const filtered = search
    ? numbers.filter(n =>
        n.phone_number.includes(search) ||
        n.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
        n.sip_uri?.toLowerCase().includes(search.toLowerCase())
      )
    : numbers;

  // Group by customer
  const grouped = {};
  for (const n of filtered) {
    const key = n.customer_name || 'Unassigned';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(n);
  }

  const configured   = numbers.filter(n => n.sip_uri).length;
  const unconfigured = numbers.length - configured;

  return (
    <div className="content">

      {/* Demo mode banner */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: '#1a1400', border: '1px solid #92400e66',
        borderRadius: 8, padding: '10px 16px', marginBottom: 20,
      }}>
        <FlaskConical size={16} color="var(--warn)" style={{ flexShrink: 0 }}/>
        <div>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--warn)' }}>Demo Mode — </span>
          <span style={{ fontSize: 13, color: 'var(--warn)' }}>
            Routing changes are saved locally only. Nothing is pushed to Bandwidth.
          </span>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid mb-20" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
        {[
          { label: 'Total Numbers',  value: numbers.length,  color: 'var(--text-2)' },
          { label: 'Configured',     value: configured,       color: 'var(--ok)' },
          { label: 'Not Configured', value: unconfigured,     color: 'var(--warn)' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: s.color }}>{loading ? '…' : s.value}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Phone size={15} color="var(--info)"/>
            <div className="card-title">Bandwidth Number Routing</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search number or customer…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                background: 'var(--bg)', border: '1px solid var(--border-strong)', borderRadius: 6,
                color: 'var(--text-2)', padding: '5px 10px', fontSize: 12, width: 200,
              }}
            />
            <button className="btn btn-sm" onClick={handleFetch} disabled={fetching || loading} title="Pull number inventory direct from Bandwidth Dashboard API">
              <Download size={11} style={fetching ? { animation: 'spin 0.8s linear infinite' } : {}}/>
              {' '}{fetching ? 'Fetching…' : 'Fetch from Bandwidth'}
            </button>
            <button className="btn btn-sm" onClick={handleSync} disabled={syncing || loading}>
              <RefreshCw size={11} style={syncing ? { animation: 'spin 0.8s linear infinite' } : {}}/>
              {' '}{syncing ? 'Syncing…' : 'Sync from CDRs'}
            </button>
            <button className="btn btn-sm" onClick={load} disabled={loading}>
              <RefreshCw size={11}/>
            </button>
          </div>
        </div>

        {error && (
          <div style={{ padding: '12px 20px', display: 'flex', gap: 8, alignItems: 'center', color: 'var(--err)', fontSize: 13 }}>
            <AlertTriangle size={14}/> {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', color: 'var(--muted-3)', padding: '40px 0' }}>Loading…</div>
        ) : numbers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <div style={{ color: 'var(--muted-3)', fontSize: 13, marginBottom: 8 }}>No numbers yet.</div>
            <div style={{ color: 'var(--muted-3)', fontSize: 12 }}>
              Click <strong style={{ color: 'var(--info)' }}>Sync from CDRs</strong> to discover Bandwidth numbers from your call history.
            </div>
          </div>
        ) : (
          Object.entries(grouped).map(([customer, nums]) => (
            <div key={customer}>
              <div style={{
                padding: '8px 20px', background: '#0d1018',
                borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)',
                fontSize: 11, fontWeight: 600, color: 'var(--info)',
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}>
                {customer} <span style={{ color: 'var(--muted-3)', fontWeight: 400, textTransform: 'none' }}>({nums.length})</span>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Phone Number</th>
                      <th>SIP Destination</th>
                      <th>Notes</th>
                      <th>Last Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nums.map(n => (
                      <tr key={n.phone_number}>
                        <td>
                          <span className="tag font-mono" style={{ fontSize: 13 }}>{n.phone_number}</span>
                        </td>
                        <td>
                          <SipUriEditor number={n.phone_number} current={n.sip_uri} onSave={handleSave}/>
                        </td>
                        <td>
                          <span style={{ fontSize: 12, color: 'var(--muted-3)' }}>{n.notes || '—'}</span>
                        </td>
                        <td style={{ fontSize: 11, color: 'var(--muted-3)', fontFamily: 'monospace' }}>
                          {n.updated_at ? n.updated_at.replace('T', ' ').slice(0, 16) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
